import React, { useCallback, useMemo, ReactNode } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Sidebar, Link } from 'shared'

import { getLinks } from 'constants/links'
import modals from 'constants/modals'

import { useModal } from 'utils/providers/ModalProvider'
import { useAuth } from 'utils/providers/AuthProvider'
import withMobile from 'utils/hocs/withMobile'

import MainMobileWrapper from './mobile'

import { QuestionMarkIcon } from 'assets/icons'
import logo from 'assets/images/logo.webp'
import Onboarding from './Onboarding'
import OnboardingProvider from 'utils/providers/OnboardingProvider'

const useStyles = makeStyles((theme) => ({
  content: {
    padding: '16px 24px 24px 24px',
    width: `calc(100% - ${theme.custom.sidebarWidth}px)`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  navbarContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: '32px',
    gap: '22px',
    '& > *': {
      width: 'unset',
      marginBottom: '0px',
    },
  },
}))

const MainWrapper = ({ children = null }: { children?: ReactNode }) => {
  const classes = useStyles()
  const location = useLocation()
  const navigate = useNavigate()
  const { showModal } = useModal()
  const { logout, user, isUserLoading } = useAuth()

  const navbarItems = useMemo(
    () => [
      {
        name: 'Settings',
        onClick: () => null,
        disabled: true,
      },
      {
        name: 'Log Out',
        onClick: logout,
        isPrimary: true,
      },
    ],
    []
  )

  const onNavigate = useCallback((link: Link) => {
    navigate(link.url)
  }, [])

  const userInfo = {
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    avatar: user?.profile?.profileImageUrl || '',
    role: 'User',
  }

  return (
    <Grid container>
      <OnboardingProvider>
        <Sidebar
          links={getLinks(user)}
          onClick={onNavigate}
          activeLink={location.pathname}
          logo={logo}
          userInfo={userInfo}
          userMenuItems={navbarItems}
          isLoading={isUserLoading}
        >
          <Onboarding />
        </Sidebar>
      </OnboardingProvider>
      <div className={classes.content}>
        <Grid className={classes.navbarContainer}>
          <QuestionMarkIcon onClick={() => showModal(modals.supportModal)} />
        </Grid>
        <Grid height="100%">{children ?? <Outlet />}</Grid>
      </div>
    </Grid>
  )
}

export default withMobile({
  DesktopComponent: MainWrapper,
  MobileComponent: MainMobileWrapper,
})
